import {
  formatBankruptcySettledLabel,
  formatBankruptcyTypeLabel,
  formatYesNoFlag,
} from "../../utils/personalHistoryDisplay";
import styles from "../../styles/applicationDetail.module.css";
import type { PersonalHistoryTabProps, AllDataShape, ApplicationDataShape } from '../../types/tabs'

const RequiredAsterisk = () => (
  <span style={{ color: "#dc2626", marginLeft: "4px" }}>*</span>
);

/** Read-only Personal History tab — Angular admin #questionView parity; edit on resident flow. */
export function PersonalHistoryTab({
  applicationAllData,
  applicationData,
}: PersonalHistoryTabProps) {
  const allData = (applicationAllData ?? null) as AllDataShape | null;
  const appData = (applicationData ?? null) as ApplicationDataShape | null;
  const isNotStarted = appData?.applicant_status === "NOT_STARTED";

  // Eviction display — Angular renders the answer paragraph only when the
  // value is in {'1','0'} or (empty AND not NOT_STARTED).
  const evictionAnswer = formatYesNoFlag(appData?.rent_debt_collection);
  const showEvictionDefaultNo =
    !evictionAnswer && !appData?.rent_debt_collection && !isNotStarted;

  // Bankruptcy main answer — Angular concatenates "Yes - {explanation}" inline
  // when value is '1' (`application-details.component.html:1334`).
  const bankruptcyFlag = formatYesNoFlag(appData?.involved_bankruptcy);
  const bankruptcyExplanation = (
    appData?.involved_bankruptcy_list ?? ""
  ).toString();
  const bankruptcyMainText =
    bankruptcyFlag === "Yes"
      ? `Yes - ${bankruptcyExplanation}`
      : bankruptcyFlag === "No"
        ? "No"
        : "";
  const showBankruptcyDefaultNo =
    !bankruptcyFlag && !appData?.involved_bankruptcy && !isNotStarted;

  // Bankruptcy sub-fields — only when value is '1'.
  const showBankruptcyDetails = bankruptcyFlag === "Yes";
  const bankruptcyTypeLabel = formatBankruptcyTypeLabel(
    appData?.bankruptcy_type,
  );
  const settledLabel = formatBankruptcySettledLabel(
    appData?.still_open_bankruptcy,
  );
  const showSettlementDate = appData?.still_open_bankruptcy === "0";

  // Children — render the value when present.
  const childrenRaw = allData?.children;
  const hasChildren =
    childrenRaw !== undefined &&
    childrenRaw !== null &&
    `${childrenRaw}`.trim() !== "";
  const childrenAnswer = hasChildren ? String(childrenRaw) : "";

  return (
    <div
      className={`${styles.tabContent} ${styles.personalHistoryPremium}`}
      data-testid="application-personal-history-tab-page"
    >
      <div
        className={styles.section}
        data-testid="application-personal-history-tab-section-eviction"
      >
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6 data-testid="application-personal-history-tab-eviction-label">
              Have you been evicted? <RequiredAsterisk />
            </h6>
            {evictionAnswer && (
              <p data-testid="application-personal-history-tab-eviction-answer">
                {evictionAnswer}
              </p>
            )}
            {showEvictionDefaultNo && (
              <p data-testid="application-personal-history-tab-eviction-answer-default-no">
                No
              </p>
            )}
          </div>
        </div>
      </div>

      <div
        className={styles.section}
        data-testid="application-personal-history-tab-section-bankruptcy"
      >
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6 data-testid="application-personal-history-tab-bankruptcy-label">
              Are you currently involved in a bankruptcy? <RequiredAsterisk />
            </h6>
            {bankruptcyMainText && (
              <p data-testid="application-personal-history-tab-bankruptcy-answer">
                {bankruptcyMainText}
              </p>
            )}
            {showBankruptcyDefaultNo && (
              <p data-testid="application-personal-history-tab-bankruptcy-answer-default-no">
                No
              </p>
            )}
          </div>
        </div>
        {showBankruptcyDetails && (
          <div
            className={styles.infoRow}
            data-testid="application-personal-history-tab-bankruptcy-details"
          >
            <div className={styles.infoColumn}>
              <h6>What type? <RequiredAsterisk /></h6>
              <p data-testid="application-personal-history-tab-bankruptcy-type">
                {bankruptcyTypeLabel || appData?.bankruptcy_type || ""}
              </p>
            </div>
            <div className={styles.infoColumn}>
              <h6>Is it Settled? <RequiredAsterisk /></h6>
              <p data-testid="application-personal-history-tab-bankruptcy-settled">
                {settledLabel}
              </p>
            </div>
            {showSettlementDate && (
              <div className={styles.infoColumn}>
                <h6>Date to be Settled?</h6>
                <p data-testid="application-personal-history-tab-bankruptcy-settlement-date">
                  {appData?.involved_bankruptcy_date_to_settle ?? ""}
                </p>
              </div>
            )}
            <div className={styles.infoColumn}>
              <h6>Explain <RequiredAsterisk /></h6>
              <p data-testid="application-personal-history-tab-bankruptcy-explain">
                {appData?.involved_bankruptcy_list ?? ""}
              </p>
            </div>
          </div>
        )}
      </div>

      <div
        className={styles.section}
        data-testid="application-personal-history-tab-section-children"
      >
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6 data-testid="application-personal-history-tab-children-label">
              How many children will reside with you? <RequiredAsterisk />
            </h6>
            {childrenAnswer && (
              <p data-testid="application-personal-history-tab-children-answer">
                {childrenAnswer}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
